


































import { computed, defineComponent, onBeforeUnmount, PropType, ref } from "@vue/composition-api";
import { TranslateResult } from "vue-i18n";
import { BlockingStatus } from "@scrile/api-provider/dist/api/BlockingStatusProvider";
import { Notification } from "@scrile/api-provider/dist/api/NotificationsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { EVENTS } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import ModalView from "@/components/ModalView";

export default defineComponent({
  name: "BlockUserController",
  components: { BaseConfirm, BaseButton, ModalView },
  props: {
    opponent: { type: Object as PropType<UserPublicData> },
    showConfirm: { type: Boolean, default: false },
    warningText: { type: String as PropType<TranslateResult | string> },
    confirmTitle: { type: String as PropType<TranslateResult | string> },
    blockButtonText: { type: String as PropType<TranslateResult | string> },
  },
  setup(props, { emit }) {
    const { authUser } = useAuthUser();

    const blockingStatus = ref<BlockingStatus[]>([]);
    const loading = ref(false);

    const opponentBlocked = computed(() =>
      blockingStatus.value.some(s => s.userId === authUser.value?.id && s.blockedUserId === props.opponent?.id)
    );
    const authUserBlocked = computed(() =>
      blockingStatus.value.some(s => s.userId === props.opponent?.id && s.blockedUserId === authUser.value?.id)
    );
    const publicBlockingStatus = computed(() => ({
      authUser: authUserBlocked.value,
      opponent: opponentBlocked.value,
    }));
    const acceptButtonText = computed(() => $t(opponentBlocked.value ? "Unblock" : "Block"));
    const blockingConfirmText = computed(() =>
      opponentBlocked.value
        ? $t("Unblock {0}", [props.opponent?.username])
        : $t("Block {0}", [props.opponent?.username])
    );

    const toggleBlockingStatus = async () => {
      if (!props.opponent) return;
      try {
        loading.value = true;

        if (opponentBlocked.value) {
          await providers.BlockingStatusProvider.unblock(props.opponent.id);
        } else {
          await providers.BlockingStatusProvider.block(props.opponent.id);
        }
        emit("blockingStatusUpdated", {
          user: props.opponent,
          isBlocked: !opponentBlocked.value,
        });
      } finally {
        loading.value = false;
        emit("close");
      }
    };

    const emitBlockingStatusUp = () => emit("blockingStatusChange", publicBlockingStatus.value);

    const setBlockingStatusToBuffer = (authUserId: string, opponentId: string, time: string) => {
      const blockingUserIndex = blockingStatus.value.findIndex(s => s.userId === authUserId);

      if (blockingUserIndex === -1) {
        blockingStatus.value.push({
          userId: authUserId,
          blockedUserId: opponentId,
          time: time,
        });
      } else {
        blockingStatus.value.splice(blockingUserIndex, 1);
      }
    };

    const accept = async () => {
      if (authUser.value && props.opponent) {
        await toggleBlockingStatus();
        setBlockingStatusToBuffer(authUser.value.id, props.opponent.id, new Date().toISOString());
        emitBlockingStatusUp();
      }
    };

    const blockingStatusChangeHandler = (notification: Notification): void => {
      const blockedUserId = notification.recipientId;
      const blockingUserId = notification.subjectId;

      if (blockingUserId !== props.opponent?.id) {
        return;
      }

      setBlockingStatusToBuffer(blockingUserId, blockedUserId, notification.time);
      emitBlockingStatusUp();
    };

    const getBlockingStatus = async () => {
      if (authUser.value && props.opponent)
        blockingStatus.value = await providers.BlockingStatusProvider.searchByOpponents({
          data: { opponentIds: [props.opponent.id] },
        });
    };

    getBlockingStatus().then(emitBlockingStatusUp);
    emitter.on<any>(EVENTS.BLOCKING_STATUS_CHANGED, blockingStatusChangeHandler);
    onBeforeUnmount(() => emitter.off<any>(EVENTS.BLOCKING_STATUS_CHANGED, blockingStatusChangeHandler));

    return {
      loading,
      opponentBlocked,
      acceptButtonText,
      blockingConfirmText,
      accept,
    };
  },
});
